import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { Subject, debounceTime, distinctUntilChanged, firstValueFrom, takeUntil } from 'rxjs';
import { ToastService } from 'angular-toastify';
import * as XLSX from 'xlsx';
import { StampBillableChargeTableRow } from '../../../../../core/models/parking/stamp-discount.model';
import { MapperStampDiscountService } from '../../../../../core/mappers/parking/mapper-stamp-discount.service';
import { MapperParkingMasterDataService } from '../../../../../core/mappers/system-settings/mapper-parking-master-data.service';
import { ApiStampDiscountService } from '../../../../../core/services/api/parking/api-stamp-discount.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import { SiteStateService } from '../../../../../core/services/site-state.service';
import {
  PssSearchablePickerComponent,
  SearchablePickerOption,
} from '../../../../../tools/template/pss/pss-searchable-picker/pss-searchable-picker.component';
import { PssLoadingComponent } from '../../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ModalInvoicePdfViewerComponent } from '../../../modals/finance/invoice/modal-invoice-pdf-viewer/modal-invoice-pdf-viewer.component';
import { ModalStampBillableChargeEditComponent } from '../../../modals/parking/modal-stamp-billable-charge-edit/modal-stamp-billable-charge-edit.component';

@Component({
  selector: 'app-stamp-billable-charge',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PssLoadingComponent,
    PssSearchablePickerComponent,
    ModalInvoicePdfViewerComponent,
    ModalStampBillableChargeEditComponent,
  ],
  templateUrl: './stamp-billable-charge.component.html',
  styleUrl: './stamp-billable-charge.component.css',
})
export class StampBillableChargeComponent implements OnInit, OnDestroy {
  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiStampDiscountService);
  private readonly mapper = inject(MapperStampDiscountService);
  private readonly actionMapper = inject(MapperParkingMasterDataService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);
  private readonly destroy$ = new Subject<void>();
  private readonly search$ = new Subject<string>();
  private readonly customerSearch$ = new Subject<string>();

  items = signal<StampBillableChargeTableRow[]>([]);
  isLoading = signal(false);
  isMarking = signal(false);
  isExporting = signal(false);
  isProcessingPdf = signal(false);
  showConfirmModal = signal(false);
  showPdfModal = signal(false);
  showEditModal = signal(false);
  editingRow = signal<StampBillableChargeTableRow | null>(null);
  pdfSrc: Uint8Array | null = null;
  pdfDownloadName = 'stamp-billable-charge.pdf';

  searchText = '';
  statusFilter: '' | 'pending' | 'invoiced' | 'cancelled' = 'pending';
  startDate = '';
  endDate = '';
  cpCustomerId: number | null = null;
  cpCustomerLabel = '';

  customerPickerOptions = signal<SearchablePickerOption[]>([]);
  customerPickerTotal = signal(0);
  customerPickerLoading = signal(false);

  pendingMatchCount = signal(0);
  pageParkingDurationMinutes = signal(0);
  sumFee = signal(0);
  sumVat = signal(0);
  sumTotal = signal(0);

  currentPage = signal(1);
  pageSize = signal(20);
  pageSizeOptions: number[] = [10, 20, 50, 100];
  totalItems = signal(0);
  totalPages = signal(1);
  lastFetchedTime: Date | null = null;

  constructor() {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    this.startDate = this.toInputDate(start);
    this.endDate = this.toInputDate(now);

    effect(() => {
      const siteId = this.siteState.selectedSite()?.id;
      untracked(() => {
        this.currentPage.set(1);
        this.cpCustomerId = null;
        this.cpCustomerLabel = '';
        this.customerPickerOptions.set([]);
        this.customerPickerTotal.set(0);
        this.items.set([]);
        this.totalItems.set(0);
        this.pendingMatchCount.set(0);
        this.pageParkingDurationMinutes.set(0);
        this.sumFee.set(0);
        this.sumVat.set(0);
        this.sumTotal.set(0);
        if (siteId) this.loadCustomerOptions('');
      });
    });
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get canUpdate(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  /** แก้รายการ (fee/status) — ยืนยันใช้สิทธิ์ Create */
  get canCreate(): boolean {
    return this.permissionService.canCreate(this.router.url);
  }

  get canSearch(): boolean {
    return !!this.selectedSiteId && !!this.cpCustomerId;
  }

  get startItemIndex(): number {
    if (this.totalItems() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage() * this.pageSize(), this.totalItems());
  }

  get pagesArray(): number[] {
    const total = this.totalPages();
    const maxVisible = 5;
    let start = Math.max(1, this.currentPage() - 2);
    let end = Math.min(total, start + maxVisible - 1);
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  get pageDurationText(): string {
    return this.mapper.formatParkingDuration(this.pageParkingDurationMinutes());
  }

  ngOnInit(): void {
    this.search$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.currentPage.set(1);
        this.loadTable();
      });
    this.customerSearch$
      .pipe(debounceTime(250), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((q) => this.loadCustomerOptions(q));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearchInput(value: string): void {
    this.searchText = value;
    if (!this.canSearch) return;
    this.search$.next(value.trim());
  }

  onFilterChange(): void {
    this.currentPage.set(1);
    this.loadTable();
  }

  onRefresh(): void {
    this.currentPage.set(1);
    this.loadTable();
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
      this.loadTable();
    }
  }

  onPageSizeChange(event: Event): void {
    this.pageSize.set(Number((event.target as HTMLSelectElement).value));
    this.currentPage.set(1);
    this.loadTable();
  }

  onCustomerOpened(): void {
    if (!this.selectedSiteId) {
      this.toast.error('กรุณาเลือกลานจากแถบด้านบน');
      return;
    }
    this.loadCustomerOptions('');
  }

  onCustomerSearch(text: string): void {
    this.customerSearch$.next(text.trim());
  }

  onCustomerSelected(opt: SearchablePickerOption): void {
    this.cpCustomerId = opt.id;
    this.cpCustomerLabel = opt.label;
    this.currentPage.set(1);
    this.loadTable();
  }

  onCustomerCleared(): void {
    this.cpCustomerId = null;
    this.cpCustomerLabel = '';
    this.items.set([]);
    this.totalItems.set(0);
    this.pendingMatchCount.set(0);
    this.pageParkingDurationMinutes.set(0);
    this.sumFee.set(0);
    this.sumVat.set(0);
    this.sumTotal.set(0);
  }

  async onExportExcel(): Promise<void> {
    if (!this.canSearch) {
      this.toast.error('กรุณาเลือกลานและลูกค้า');
      return;
    }
    if (this.totalItems() <= 0) {
      this.toast.error('ไม่มีข้อมูลให้ส่งออก');
      return;
    }

    this.isExporting.set(true);
    try {
      const rows = await this.fetchAllRowsForExport();
      if (rows.length === 0) {
        this.toast.error('ไม่มีข้อมูลให้ส่งออก');
        return;
      }

      const exportRows: Record<string, string | number>[] = rows.map((item) => ({
        'วันที่เรียกเก็บ': this.formatDateTime(item.chargeDate),
        'ทะเบียน': item.entryLicense || '',
        'ธุรกรรม': item.parkingTransactionId,
        'กลุ่ม': item.groupName || '',
        'รหัสเรท': item.rateId || '',
        'ชื่อเรท': item.rateName || '',
        'เวลาเข้า': this.formatDateTime(item.entryDate),
        'เวลาออก': item.exitDate ? this.formatDateTime(item.exitDate) : 'ยังอยู่ในลาน',
        'เวลาจอดรวม': this.durationText(item.parkingDurationMinutes),
        'ค่าธรรมเนียม': item.fee,
        'ภาษีมูลค่าเพิ่ม': item.vat,
        'รวม': item.total,
        'สถานะ': this.statusLabel(item.status),
        'หมายเหตุ': item.remark || '',
      }));

      exportRows.push({
        'วันที่เรียกเก็บ': '',
        'ทะเบียน': '',
        'ธุรกรรม': '',
        'กลุ่ม': '',
        'รหัสเรท': '',
        'ชื่อเรท': 'ยอดรวมทั้งชุด',
        'เวลาเข้า': '',
        'เวลาออก': '',
        'เวลาจอดรวม': '',
        'ค่าธรรมเนียม': this.sumFee(),
        'ภาษีมูลค่าเพิ่ม': this.sumVat(),
        'รวม': this.sumTotal(),
        'สถานะ': `${rows.length} รายการ`,
        'หมายเหตุ': '',
      });

      const worksheet = XLSX.utils.json_to_sheet(exportRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'รายงานเรียกเก็บ');
      const siteName = (this.siteState.selectedSite()?.name || 'site').replace(/\s+/g, '_');
      const fileName = `Stamp_Billable_${siteName}_${this.startDate}_${this.endDate}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      this.toast.success('ส่งออก Excel สำเร็จ');
    } catch {
      this.toast.error('ส่งออก Excel ไม่สำเร็จ');
    } finally {
      this.isExporting.set(false);
    }
  }

  onPrintPdf(): void {
    if (!this.canSearch) {
      this.toast.error('กรุณาเลือกลานและลูกค้า');
      return;
    }
    if (!this.startDate || !this.endDate) {
      this.toast.error('กรุณาเลือกช่วงวันที่');
      return;
    }

    this.isProcessingPdf.set(true);
    const siteName = (this.siteState.selectedSite()?.name || 'site').replace(/\s+/g, '_');
    this.pdfDownloadName = `stamp-billable-charge_${siteName}_${this.startDate}_${this.endDate}.pdf`;

    this.api.getStampBillableChargePdf({
      siteId: this.selectedSiteId,
      cpCustomerId: this.cpCustomerId!,
      startDate: this.startDate,
      endDate: this.endDate,
      status: this.statusFilter || undefined,
      searchText: this.searchText.trim() || undefined,
    }).subscribe({
      next: (bytes) => {
        this.pdfSrc = bytes;
        this.showPdfModal.set(true);
        this.isProcessingPdf.set(false);
      },
      error: (err) => {
        console.error('[API Error] getStampBillableChargePdf:', err);
        this.toast.error('ไม่สามารถดึงไฟล์ PDF ได้');
        this.isProcessingPdf.set(false);
      },
    });
  }

  closePdfModal(): void {
    this.showPdfModal.set(false);
    this.pdfSrc = null;
  }

  openEditRow(item: StampBillableChargeTableRow): void {
    if (!this.canCreate) {
      this.toast.error('ไม่มีสิทธิ์แก้ไขรายการ');
      return;
    }
    this.editingRow.set(item);
    this.showEditModal.set(true);
  }

  closeEditModal(): void {
    this.showEditModal.set(false);
    this.editingRow.set(null);
  }

  onEditSaved(): void {
    this.closeEditModal();
    this.loadTable();
  }

  openConfirmMarkInvoiced(): void {
    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    if (!this.canSearch) {
      this.toast.error('กรุณาเลือกลานและลูกค้า');
      return;
    }
    if (!this.startDate || !this.endDate) {
      this.toast.error('กรุณาเลือกช่วงวันที่');
      return;
    }
    if (this.pendingMatchCount() <= 0) {
      this.toast.error('ไม่มีรายการรอดำเนินการในช่วงวันที่เลือก');
      return;
    }
    this.showConfirmModal.set(true);
  }

  closeConfirmModal(): void {
    if (this.isMarking()) return;
    this.showConfirmModal.set(false);
  }

  confirmMarkInvoiced(): void {
    const userId = Number(this.cookieService.get('user_id') || 0);
    const menuId = this.permissionService.getMenuId(this.router.url) || 0;
    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลสิทธิ์ผู้ใช้งาน');
      return;
    }
    if (!this.cpCustomerId || !this.selectedSiteId) return;

    this.isMarking.set(true);
    this.api.markStampBillableChargeInvoiced({
      userId,
      menuId,
      siteId: this.selectedSiteId,
      cpCustomerId: this.cpCustomerId,
      startDate: this.startDate,
      endDate: this.endDate,
    }).subscribe({
      next: (res) => {
        this.isMarking.set(false);
        const mapped = this.actionMapper.mapActionResponse(res);
        if (mapped.isSuccess) {
          this.toast.success(mapped.message || 'เปลี่ยนสถานะสำเร็จ');
          this.showConfirmModal.set(false);
          this.loadTable();
        } else {
          this.toast.error(mapped.message || 'เปลี่ยนสถานะไม่สำเร็จ');
        }
      },
      error: (err) => {
        this.isMarking.set(false);
        this.toast.error(err?.error?.message || 'เปลี่ยนสถานะไม่สำเร็จ');
      },
    });
  }

  statusClass(status: string): string {
    switch ((status || '').toLowerCase()) {
      case 'pending':
        return 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
      case 'invoiced':
        return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400';
      case 'cancelled':
        return 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400';
      default:
        return 'bg-slate-50 text-slate-500';
    }
  }

  statusLabel(status: string): string {
    return this.mapper.statusLabelTh(status);
  }

  durationText(minutes: number | null): string {
    return this.mapper.formatParkingDuration(minutes);
  }

  trackById(_index: number, item: StampBillableChargeTableRow): number {
    return item.id;
  }

  loadTable(): void {
    if (!this.canSearch) {
      this.items.set([]);
      this.totalItems.set(0);
      this.pendingMatchCount.set(0);
      this.pageParkingDurationMinutes.set(0);
      this.sumFee.set(0);
      this.sumVat.set(0);
      this.sumTotal.set(0);
      return;
    }

    this.isLoading.set(true);
    this.api.getStampBillableChargeForTable({
      page: this.currentPage(),
      pageSize: this.pageSize(),
      startDate: this.startDate || undefined,
      endDate: this.endDate || undefined,
      status: this.statusFilter || undefined,
      cpCustomerId: this.cpCustomerId,
      siteId: this.selectedSiteId,
      searchText: this.searchText.trim() || undefined,
    }).subscribe({
      next: (res) => {
        const mapped = this.mapper.mapBillableChargeTable(res);
        this.items.set(mapped.data);
        this.totalItems.set(mapped.total);
        this.totalPages.set(Math.max(1, Math.ceil(mapped.total / this.pageSize())));
        this.pendingMatchCount.set(mapped.pendingMatchCount);
        this.pageParkingDurationMinutes.set(mapped.pageParkingDurationMinutes);
        this.sumFee.set(mapped.sumFee);
        this.sumVat.set(mapped.sumVat);
        this.sumTotal.set(mapped.sumTotal);
        this.lastFetchedTime = new Date();
        this.isLoading.set(false);
      },
      error: () => {
        this.items.set([]);
        this.isLoading.set(false);
        this.toast.error('โหลดรายงานล้มเหลว');
      },
    });
  }

  private async fetchAllRowsForExport(): Promise<StampBillableChargeTableRow[]> {
    const pageSize = 100;
    let page = 1;
    let total = Infinity;
    const all: StampBillableChargeTableRow[] = [];

    while (all.length < total) {
      const res = await firstValueFrom(this.api.getStampBillableChargeForTable({
        page,
        pageSize,
        startDate: this.startDate || undefined,
        endDate: this.endDate || undefined,
        status: this.statusFilter || undefined,
        cpCustomerId: this.cpCustomerId,
        siteId: this.selectedSiteId,
        searchText: this.searchText.trim() || undefined,
      }));
      const mapped = this.mapper.mapBillableChargeTable(res);
      total = mapped.total;
      all.push(...mapped.data);
      if (mapped.data.length === 0) break;
      page += 1;
      if (page > 200) break; // safety
    }

    return all;
  }

  private formatDateTime(value: string | null | undefined): string {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
  }

  private loadCustomerOptions(searchText: string): void {
    const siteId = this.selectedSiteId;
    if (!siteId) {
      this.customerPickerOptions.set([]);
      this.customerPickerTotal.set(0);
      return;
    }
    this.customerPickerLoading.set(true);
    this.api.getStampBillableCustomerOptions(siteId, searchText || undefined).subscribe({
      next: (res) => {
        const mapped = this.mapper.mapBillableCustomerOptions(res);
        this.customerPickerOptions.set(
          mapped.data.map((c) => ({
            id: c.id,
            label: c.label,
          }))
        );
        this.customerPickerTotal.set(mapped.total);
        this.customerPickerLoading.set(false);
      },
      error: () => {
        this.customerPickerOptions.set([]);
        this.customerPickerTotal.set(0);
        this.customerPickerLoading.set(false);
      },
    });
  }

  private toInputDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
}
